import { useEffect, useState } from "react";

/**
 * Namespace for everything this app puts in browser storage, versioned so a
 * future shape change can be rolled forward by bumping the prefix rather than
 * by defensive-parsing old payloads forever.
 */
const PREFIX = "dealflow:v1:";

/**
 * React state that survives a reload, backed by {@code localStorage}.
 *
 * <p>Only ever store UI preferences here — which filter a user last picked,
 * which panel they had open. Client data (prospect names, email bodies, search
 * terms that may contain either) belongs in the database, never on the
 * viewer's disk where no retention or erasure policy reaches it.
 *
 * <p>{@code revive} validates whatever was found in storage. Returning null
 * from it, corrupt JSON, and storage being unavailable (private windows,
 * blocked site data) all resolve the same way: fall back and carry on. Storage
 * is a convenience, so it must never be able to break a render.
 */
export function usePersistedState<T>(
  key: string,
  fallback: T,
  revive: (stored: unknown) => T | null,
): [T, (update: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(PREFIX + key);
      if (raw === null) return fallback;
      return revive(JSON.parse(raw)) ?? fallback;
    } catch {
      return fallback;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
      // Quota exceeded or storage blocked — the preference just doesn't stick.
    }
  }, [key, value]);

  return [value, setValue];
}
